import { Grid, csx, useMediaQuery } from '@vtex/admin-ui'
import React, { useState, useEffect } from 'react'

import { ProductCarouselButton } from './ProductCarouselButton'
import classNames from 'classnames'

const PRODUCTS_PER_PAGE_DEFAULT = 4

export const useDefaultMobileCardSize = () => {
  const [smallerThan400] = useMediaQuery([
    '(max-width: 400px)',
    '(min-width: 401px)',
  ])

  if (smallerThan400) {
    return '10rem'
  }

  return '11.4rem'
}

export type ProductCarouselListProps = {
  mobileCardSize?: string
  desktopProductsPerPage?: number
} & React.ComponentPropsWithoutRef<'div'>

export const ProductCarouselList = ({
  children,
  mobileCardSize,
  desktopProductsPerPage,
  className,
  ...props
}: ProductCarouselListProps) => {
  const [currentProductsListPage, setCurrentProductsListPage] = useState(0)
  const childrenCount = React.Children.count(children)

  const [translateXPercentage, setTranslateXPercentage] = useState(0)
  const [offset, setOffset] = useState(0)

  const productsPerPage = desktopProductsPerPage || PRODUCTS_PER_PAGE_DEFAULT

  const defaultMobileCardSize = useDefaultMobileCardSize()

  const handleNextProductsClick = () => {
    setCurrentProductsListPage((prev) => prev + 1)
  }

  const handlePreviousProductsClick = () => {
    setCurrentProductsListPage((prev) => prev - 1)
  }

  useEffect(() => {
    setTranslateXPercentage(-100 * currentProductsListPage)

    // Correct for last page. Example:
    // 4 products, 3 per page
    // [ 1 2 3 ] 4 X Y
    // click next page would result in:
    // 1 2 3 [ 4 X Y ]
    // Here diff = 2/3 since lastSlotPosition (Y) = 6, childrenCount = 4
    // and productsPerPage = 3. So correcting by 2/3 yields:
    // 1 [ 2 3 4 ] X Y
    // Keep offset until we're back to the first page.
    const lastSlotPosition = productsPerPage * (1 + currentProductsListPage)
    if (currentProductsListPage > 0 && lastSlotPosition > childrenCount) {
      const emptyPercentage =
        (lastSlotPosition - childrenCount) / productsPerPage
      setOffset(100 * emptyPercentage)
    } else if (currentProductsListPage === 0) {
      setOffset(0)
    }
  }, [currentProductsListPage, childrenCount, productsPerPage])

  return (
    <div
      className={classNames(
        csx({
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }),
        className
      )}
    >
      <div
        className={csx({
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          '::-webkit-scrollbar': {
            display: 'none',
          },
          '@desktop': {
            overflow: 'hidden',
          },
        })}
      >
        <Grid
          className={csx({
            transform: `translateX(${translateXPercentage + offset}%)`,
            display: 'inline-grid',
            gridAutoFlow: 'column',
            gridAutoColumns: mobileCardSize || defaultMobileCardSize,
            margin: '-0.5rem',
            '@desktop': {
              display: 'grid',
              transition: 'transform 0.4s ease',
              gridAutoColumns: `${100 / productsPerPage}%`,
              margin: '-1.25rem',
            },
          })}
          {...props}
        >
          {children}
        </Grid>
      </div>
      {currentProductsListPage > 0 && (
        <ProductCarouselButton
          direction="previous"
          onClick={handlePreviousProductsClick}
        />
      )}
      {childrenCount > productsPerPage * (1 + currentProductsListPage) && (
        <ProductCarouselButton
          direction="next"
          onClick={handleNextProductsClick}
        />
      )}
    </div>
  )
}
